
import React, { useRef, useState, useEffect } from 'react';
import { useEnhancedAuthContext } from '@/context/auth/EnhancedAuthContext';
import { AuthGuardConfig } from '@/context/auth/rbacTypes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Shield, Lock, AlertTriangle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '@/context/auth/types';
import { permissionEngine } from '@/services/auth/permissionEngine';
import { PermissionLoadError } from './PermissionLoadError';

interface AuthGuardProps extends AuthGuardConfig {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requireAuth = true,
  requiredRoles = [],
  requiredPermissions = [],
  accessContext,
  fallbackComponent: FallbackComponent,
  redirectTo,
  fallback,
}) => {
  const {
    user,
    isAuthenticated,
    hasRole,
    hasPermission,
    logSecurityEvent,
    isLoadingPermissions,
    permissionsLoaded,
  } = useEnhancedAuthContext();
  const navigate = useNavigate();

  const selfHealAttemptedRef = useRef(false);
  const [repairing, setRepairing] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const loadingTimeoutRef = useRef<number>();

  // Set timeout for permission loading — must be before any conditional returns (Rules of Hooks)
  useEffect(() => {
    if (isAuthenticated && (requiredRoles.length > 0 || requiredPermissions.length > 0) && isLoadingPermissions) {
      loadingTimeoutRef.current = window.setTimeout(() => {
        console.error('⏱️ AuthGuard: Permission loading timeout');
        setLoadingTimeout(true);
      }, 10000);

      return () => {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      };
    }
  }, [isAuthenticated, requiredRoles.length, requiredPermissions.length, isLoadingPermissions]);

  const trySelfHeal = () => {
    if (selfHealAttemptedRef.current || !user) return false;
    selfHealAttemptedRef.current = true;
    console.warn('🩺 RBAC self-heal: clearing and reloading permissions...', { userId: user.id });
    try {
      permissionEngine.clearUserCache(user.id);
      setRepairing(true);
      permissionEngine.preloadUserPermissions(user).finally(() => {
        setRepairing(false);
      });
    } catch (e) {
      console.error('RBAC self-heal error', e);
      setRepairing(false);
    }
    return true;
  };

  // Check authentication first
  if (requireAuth && !isAuthenticated) {
    logSecurityEvent({
      type: 'access_denied',
      metadata: { reason: 'not_authenticated', requiredRoles, requiredPermissions }
    });

    if (redirectTo) {
      navigate(redirectTo);
      return null;
    }

    if (FallbackComponent) {
      return <FallbackComponent />;
    }

    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Alert className="max-w-md">
          <Lock className="h-4 w-4" />
          <AlertTitle>Authentication Required</AlertTitle>
          <AlertDescription>
            You must be logged in to access this content.
          </AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/login')} className="mt-4">
          Go to Login
        </Button>
      </div>
    );
  }

  // If we are repairing permissions, show loading
  if (isAuthenticated && repairing) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <div className="text-center">
          <h3 className="text-lg font-medium">Refreshing Permissions</h3>
          <p className="text-muted-foreground">Revalidating your access...</p>
        </div>
      </div>
    );
  }

  // If authenticated but permissions are required and not loaded yet
  if (isAuthenticated && (requiredRoles.length > 0 || requiredPermissions.length > 0) && !permissionsLoaded) {
    // If loading timeout occurred, show error with retry option
    if (loadingTimeout) {
      return (
        <PermissionLoadError
          onRetry={() => {
            setLoadingTimeout(false);
            selfHealAttemptedRef.current = false;
            if (user) {
              trySelfHeal();
            }
          }}
          onContinue={requiredPermissions.length === 0 ? () => {
            // Allow continuation if only roles are required, not specific permissions
            setLoadingTimeout(false);
          } : undefined}
        />
      );
    }

    // Optimistic rendering: allow page to render while permissions load
    // The page will handle its own loading state and permission checks
    // Only block if we explicitly need to wait for critical permissions
    console.log('🔄 AuthGuard: Optimistically rendering children while permissions load');
    return <>{children}</>;
  }

  // If no permissions are required, allow access immediately after authentication
  if (isAuthenticated && requiredRoles.length === 0 && requiredPermissions.length === 0) {
    return <>{children}</>;
  }

  // Check permissions only after they're loaded
  if (isAuthenticated && permissionsLoaded && (requiredRoles.length > 0 || requiredPermissions.length > 0)) {
    // Check roles - convert string array to UserRole array
    if (requiredRoles.length > 0 && !hasRole(requiredRoles as UserRole[])) {
      logSecurityEvent({
        type: 'access_denied',
        metadata: { reason: 'insufficient_role', requiredRoles, requiredPermissions }
      });

      // One-time self-heal: clear and reload permissions if something seems off
      if (trySelfHeal()) {
        return (
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="text-center">
              <h3 className="text-lg font-medium">Refreshing Permissions</h3>
              <p className="text-muted-foreground">Revalidating your access...</p>
            </div>
          </div>
        );
      }

      if (FallbackComponent) {
        return <FallbackComponent />;
      }

      if (fallback) {
        return <>{fallback}</>;
      }

      return (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <Alert className="max-w-md" variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>Insufficient Permissions</AlertTitle>
            <AlertDescription>
              You don't have the required role to access this content.
              Required roles: {requiredRoles.join(', ')}
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    // Check permissions
    if (requiredPermissions.length > 0) {
      const hasAllPermissions = requiredPermissions.every(permission =>
        hasPermission(permission, accessContext)
      );

        if (!hasAllPermissions) {
          logSecurityEvent({
            type: 'access_denied',
            metadata: { reason: 'insufficient_permissions', requiredRoles, requiredPermissions }
          });

          // One-time self-heal: clear and reload permissions if something seems off
          if (trySelfHeal()) {
            return (
              <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="text-center">
                  <h3 className="text-lg font-medium">Refreshing Permissions</h3>
                  <p className="text-muted-foreground">Revalidating your access...</p>
                </div>
              </div>
            );
          }

          if (FallbackComponent) {
            return <FallbackComponent />;
          }

          if (fallback) {
            return <>{fallback}</>;
          }

          return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <Alert className="max-w-md" variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Access Denied</AlertTitle>
                <AlertDescription>
                  You don't have the required permissions to access this content.
                  Required permissions: {requiredPermissions.join(', ')}
                </AlertDescription>
              </Alert>
            </div>
          );
        }
    }
  }

  // All checks passed, render children
  return <>{children}</>;
};

// Convenience components for common use cases
export const AdminGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback
}) => (
  <AuthGuard requiredRoles={['admin']} fallback={fallback}>
    {children}
  </AuthGuard>
);

export const SuperuserGuard: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
  children,
  fallback
}) => (
  <AuthGuard requiredPermissions={['system.manage']} fallback={fallback}>
    {children}
  </AuthGuard>
);

export const EmailAccessGuard: React.FC<{ children: React.ReactNode; folder?: string }> = ({
  children,
  folder = 'inbox'
}) => (
  <AuthGuard
    requiredPermissions={['email.read']}
    accessContext={{ resource: 'email', metadata: { folder } }}
  >
    {children}
  </AuthGuard>
);
